// PO7-S1 (docs/build-briefs/PO7-S1-invoice-attach.md). Pure helpers for the
// invoice card: the client-side file check (a courtesy; the server's check
// is the control, ADR-013 Decision 1), and the plain-language text of a
// file row and its duplicate warning. No DOM, no network.

/** ADR-013's limits. The server holds the authoritative copy of both. */
export const INVOICE_MAX_BYTES = 25 * 1024 * 1024;

const TYPE_BY_EXTENSION: Readonly<Record<string, string>> = {
  pdf: "application/pdf",
  jpg: "image/jpeg",
  jpeg: "image/jpeg",
  png: "image/png",
  heic: "image/heic",
};

const ACCEPTED_TYPES: readonly string[] = [
  "application/pdf",
  "image/jpeg",
  "image/png",
  "image/heic",
];

/** The file picker's accept list (types plus extensions, since a phone can
 * hand over a HEIC file with an empty type). */
export const INVOICE_FILE_ACCEPT =
  "application/pdf,image/jpeg,image/png,image/heic,.pdf,.jpg,.jpeg,.png,.heic";

export type InvoiceFileCheck = { ok: true; contentType: string } | { ok: false; message: string };

/** A courtesy check before any bytes are sent. A file whose declared type is
 * empty (some HEIC photos) is typed from its extension; one whose type is
 * declared but not accepted is refused. */
export function checkInvoiceFileBeforeUpload(file: {
  name: string;
  type: string;
  size: number;
}): InvoiceFileCheck {
  const declared = file.type.trim().toLowerCase();
  const extension = file.name.includes(".") ? file.name.split(".").pop()!.toLowerCase() : "";
  const contentType = declared.length > 0 ? declared : (TYPE_BY_EXTENSION[extension] ?? "");
  if (!ACCEPTED_TYPES.includes(contentType)) {
    return {
      ok: false,
      message: "This file type is not accepted. Attach a PDF, JPEG, PNG or HEIC file.",
    };
  }
  if (file.size === 0) {
    return { ok: false, message: "This file is empty. Pick the file again." };
  }
  if (file.size > INVOICE_MAX_BYTES) {
    return {
      ok: false,
      message: `This file is larger than the ${INVOICE_MAX_BYTES / (1024 * 1024)} MB limit.`,
    };
  }
  return { ok: true, contentType };
}

export function formatInvoiceFileSize(bytes: number): string {
  if (bytes < 1024 * 1024) {
    return `${Math.max(1, Math.round(bytes / 1024))} KB`;
  }
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

const MONTHS = [
  "Jan",
  "Feb",
  "Mar",
  "Apr",
  "May",
  "Jun",
  "Jul",
  "Aug",
  "Sept",
  "Oct",
  "Nov",
  "Dec",
];

/** `Sept 22, 2026`, in UTC like every other date on the purchasing screens. */
export function formatInvoiceDate(iso: string): string {
  const date = new Date(iso);
  return `${MONTHS[date.getUTCMonth()]} ${date.getUTCDate()}, ${date.getUTCFullYear()}`;
}

/** `Uploaded 2 min ago by Jordan T. · 340 KB`. */
export function describeInvoiceUpload(
  invoice: { uploadedAt: string; uploadedByName: string | null; sizeBytes: number },
  now: Date,
): string {
  const minutes = Math.floor((now.getTime() - new Date(invoice.uploadedAt).getTime()) / 60000);
  const when =
    minutes < 1
      ? "just now"
      : minutes < 60
        ? `${minutes} min ago`
        : `on ${formatInvoiceDate(invoice.uploadedAt)}`;
  const who = invoice.uploadedByName === null ? "" : ` by ${invoice.uploadedByName}`;
  return `Uploaded ${when}${who} · ${formatInvoiceFileSize(invoice.sizeBytes)}`;
}

export interface InvoiceDuplicateInfo {
  orderId: string;
  vendorName: string;
  placedAt: string | null;
  /** Null for a file already on another location's order (the date is not shown across locations). */
  attachedAt: string | null;
}

/** The warning for a byte-identical file already attached to another order
 * (design/mockups/po-merge-invoice-receiving-attach.html, adapted: no invoice
 * number exists before OCR, so the file itself is what matched). */
export function describeInvoiceDuplicate(duplicate: InvoiceDuplicateInfo): {
  title: string;
  detail: string;
} {
  const placed =
    duplicate.placedAt === null ? "" : `, placed ${formatInvoiceDate(duplicate.placedAt)}`;
  return {
    title: "This file is already on file for another order.",
    detail:
      (duplicate.attachedAt === null
        ? `It is already on file for the ${duplicate.vendorName} order${placed}. `
        : `It was attached on ${formatInvoiceDate(duplicate.attachedAt)} to the ${duplicate.vendorName} order${placed}. `) +
      "Check that this invoice belongs on this order too.",
  };
}
